import { create } from "zustand";

import {
  DEFAULT_SIDEBAR_VIEW_FILTERS,
  SIDEBAR_ENVIRONMENT_FILTERS,
  SIDEBAR_GROUP_BY_OPTIONS,
  SIDEBAR_STATUS_FILTERS,
  type SidebarViewFilters,
} from "./components/sidebar/sidebarViewFilters";

/**
 * Persisted separately from `uiStateStore` on purpose: that module is already
 * ~850 lines, and the filter vocabularies live next to the sidebar logic they
 * describe. Keeping this store here avoids importing sidebar modules into the
 * global UI store, which is how import cycles start.
 */
const PERSISTED_KEY = "forge.sidebar.viewFilters.v1";

/**
 * Reads one persisted value back, falling back to the default whenever the
 * stored value is not a member of the vocabulary. Storage is user-writable and
 * survives across releases, so a value that has since been renamed or removed
 * must not be trusted into the store.
 */
function readMember<Value extends string>(
  raw: Record<string, unknown>,
  key: string,
  allowed: readonly Value[],
  fallback: Value,
): Value {
  const value = raw[key];
  return typeof value === "string" && (allowed as readonly string[]).includes(value)
    ? (value as Value)
    : fallback;
}

export function parsePersistedSidebarViewFilters(serialized: string | null): SidebarViewFilters {
  if (serialized === null) return DEFAULT_SIDEBAR_VIEW_FILTERS;
  let raw: unknown;
  try {
    raw = JSON.parse(serialized);
  } catch {
    return DEFAULT_SIDEBAR_VIEW_FILTERS;
  }
  if (typeof raw !== "object" || raw === null) return DEFAULT_SIDEBAR_VIEW_FILTERS;
  const record = raw as Record<string, unknown>;
  return {
    status: readMember(
      record,
      "status",
      SIDEBAR_STATUS_FILTERS,
      DEFAULT_SIDEBAR_VIEW_FILTERS.status,
    ),
    environment: readMember(
      record,
      "environment",
      SIDEBAR_ENVIRONMENT_FILTERS,
      DEFAULT_SIDEBAR_VIEW_FILTERS.environment,
    ),
    groupBy: readMember(
      record,
      "groupBy",
      SIDEBAR_GROUP_BY_OPTIONS,
      DEFAULT_SIDEBAR_VIEW_FILTERS.groupBy,
    ),
    showEmptyWorkspaces:
      typeof record.showEmptyWorkspaces === "boolean"
        ? record.showEmptyWorkspaces
        : DEFAULT_SIDEBAR_VIEW_FILTERS.showEmptyWorkspaces,
  };
}

function loadInitialFilters(): SidebarViewFilters {
  if (typeof window === "undefined") return DEFAULT_SIDEBAR_VIEW_FILTERS;
  try {
    return parsePersistedSidebarViewFilters(window.localStorage.getItem(PERSISTED_KEY));
  } catch {
    return DEFAULT_SIDEBAR_VIEW_FILTERS;
  }
}

function persistFilters(filters: SidebarViewFilters): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(PERSISTED_KEY, JSON.stringify(filters));
  } catch {
    // Ignore quota/storage errors — a lost filter preference must never break
    // the sidebar.
  }
}

interface SidebarViewFiltersStore {
  readonly filters: SidebarViewFilters;
  readonly setFilters: (filters: SidebarViewFilters) => void;
}

export const useSidebarViewFiltersStore = create<SidebarViewFiltersStore>((set) => ({
  filters: loadInitialFilters(),
  setFilters: (filters) => {
    persistFilters(filters);
    set({ filters });
  },
}));
